import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { Editor } from 'slate-react';
import { connect } from 'react-redux';
import propTypes from 'prop-types';
import { markTypesToCheck } from '@components/userDocuments/editDocument/editJournal/slateUtilities/renderMarksEnums';
import { clearGlobalSelection } from '@utility/clearGlobalSelection';
import {
  INSERT_TEXT,
  SET_SELECTION,
  ADD_MARK,
  REMOVE_MARK,
} from '../../../../../../../../constants/selectionOpts';
import renderBlock from '../EditorSection/renderBlock';
import Schema from '../EditorSection/Schema';
import Actions from '../../../../../../../../store/actions';
import paste from '../EditorSection/utils/paste';
import KEY_CODES from '../../../../../editJournal/constants/keyCodes';
import { TYPING_DEBOUNCE } from '../../../../../../../../constants/debounce';
import breakpoints from '../../../../../../../../styling/breakpoints';
import undoRedo from '../EditorSection/utils/undoRedo';
import stopCommentExtension from '../../../../../../../../utility/commentExtension';
import CommentsCard from '../../../../../../../comments/commentsCard';
import resumePlugins from '../EditorSection/resumePlugins';
import renderMark from '../../../../../editJournal/slateUtilities/renderMark';
import setMarksChange from './setMarksChange';
import setMarksOnBlur from '../EditorSection/setMarksOnBlur';
import FormatTypes from '../../../../../../../../constants/templateDictionaries/formatTypes';
import enter from '../EditorSection/utils/enter';
import tab from '../EditorSection/utils/tab';
import commentChange from '../EditorSection/commentChange';
import SnackBarAlerts from '../../../../../../../common/snackBarAlerts/snackBarAlerts';
import removeSelectedNodes from '../EditorSection/utils/removeSelectedNodes';
import backspace from '../EditorSection/utils/backspace';
import { nodeToInputNames } from '../../../../../../../../store/helpers/titleToSectionKeyDict';
import breakpointsMinWidth from '../../../../../../../../styling/breakpointsMinWidth';
import { fnBrowserDetect } from '../../../../../../../../utility/browsers';
import { CHROME, SAFARI } from '../../../../../../../../utility/enums/browsers';
import dateComponents from '../../../../../../../../constants/dateComponents';
import allowedKeyCodes from '../../../../../../../../constants/allowedKeyCodes';
import removeMarksFromValue from '../EditorSection/utils/removeMarksFromValue';

const InputWrapper = styled.div`
  position: relative;
  grid-column: 2/-1;
  cursor: text;
  box-sizing: border-box;
  border-radius: 4px;
  justify-self: ${(props) =>
    props.componentKey === 'address' ? 'flex-start' : 'baseline'};
  height: 40px;
  width: -webkit-fill-available;
  width: -moz-available;
  width: 100%;
  text-align: left;
  pointer-events: auto;
  cursor: default;
  ${(props) => {
    const { formatType } = props;

    switch (formatType) {
      case FormatTypes.COVER_LETTER: {
        return css`
          grid-column: 1/-1;
        `;
      }
      default:
        return null;
    }
  }}

  ${(props) => {
    const { componentKey } = props;

    if (
      componentKey === 'openGreeting' ||
      componentKey === 'closeGreeting' ||
      componentKey === 'receiverName' ||
      componentKey === 'senderEmail'
    ) {
      return css`
        grid-column: 1/3;
      `;
    }

    if (componentKey === 'letterDate') {
      return css`
        grid-column: 3/-1;
      `;
    }

    if (componentKey === 'fullName') {
      let fullNameStyles = '';
      const commonStyles = css`
        grid-column: 1/-1;
      `;
      const { showImage } = props;
      if (showImage) {
        fullNameStyles = css`
          @media ${breakpointsMinWidth.tabletSm} {
            grid-column: 2/-1;
          }
          @media ${breakpointsMinWidth.desktopMd} {
            grid-column: 3/8;
          }
        `;
      } else {
        fullNameStyles = css`
          @media ${breakpointsMinWidth.tabletSm} {
            grid-column: 1/-1;
          }
          @media ${breakpointsMinWidth.desktopMd} {
            grid-column: 1/7;
          }
        `;
      }
      return css`
        ${commonStyles};
        ${fullNameStyles};
      `;
    }
    if (componentKey === 'tagline' || componentKey === 'phoneNumber') {
      let taglineStyles = '';
      const commonStyles = css`
        grid-column: 1/-1;
      `;
      const { showImage } = props;
      if (showImage) {
        taglineStyles = css`
          @media ${breakpointsMinWidth.tabletSm} {
            grid-column: 2/-1;
          }
          @media ${breakpointsMinWidth.desktopMd} {
            grid-column: 8/-1;
          }
        `;
      } else {
        taglineStyles = css`
          @media ${breakpointsMinWidth.tabletSm} {
            grid-column: 1/-1;
          }
          @media ${breakpointsMinWidth.desktopMd} {
            grid-column: 7/-1;
          }
        `;
      }
      return css`
        ${commonStyles};
        ${taglineStyles};
      `;
    }
    if (componentKey === 'address') {
      return css`
        grid-column: 1/-1;
      `;
    }
    if (componentKey === 'subjectLine') {
      return css`
        grid-column: 1/-1;
      `;
    }

    if (componentKey === 'email') {
      let emailStyles = '';
      const commonStyles = css`
        grid-column: 1/-1;
        @media ${breakpointsMinWidth.tabletSm} {
          grid-column: 1/3;
        }
        @media ${breakpointsMinWidth.tabletLg} {
          grid-column: 1/4;
        }
      `;
      const { showImage } = props;
      if (showImage) {
        emailStyles = css`
          @media ${breakpointsMinWidth.desktopMd} {
            grid-column: 3/8;
          }
        `;
      } else {
        emailStyles = css`
          @media ${breakpointsMinWidth.desktopMd} {
            grid-column: 1/7;
          }
        `;
      }
      return css`
        ${commonStyles};
        ${emailStyles};
      `;
    }

    if (componentKey === 'objective') {
      return css`
        grid-column: 1/-1;
        justify-self: flex-start;
        min-height: 7rem;
        height: auto;
      `;
    }

    if (componentKey === 'letterBody') {
      return css`
        grid-column: 1/-1;
        justify-self: flex-start;
        min-height: 7rem;
        height: auto;
      `;
    }
    return null;
  }}

  ${(props) => {
    const { componentKey } = props;
    if (componentKey !== 'letterBody' && componentKey !== 'objective') {
      return css`
        @media ${breakpointsMinWidth.tabletSm} {
          height: 36px;
        }

        @media ${breakpointsMinWidth.desktopMd} {
          height: 40px;
        }
      `;
    }
    return css``;
  }}
`;

const Input = styled.div`
  height: 100%;
  cursor: text;
  ${(props) => {
    const { componentKey } = props;
    if (componentKey === 'objective') {
      return css`
        height: auto;
      `;
    }

    if (componentKey === 'letterBody') {
      return css`
        height: auto;
        min-height: 7rem;
      `;
    }

    return null;
  }}

  @supports (-ms-ime-align: auto) {
    width: 25vw;
  }

  @media ${breakpoints.desktopLg} {
    ${(props) => {
      const { componentKey } = props;

      if (componentKey === 'letterBody' || componentKey === 'objective') {
        return css`
          height: auto;
        `;
      }

      return null;
    }}

    ${() => css`
      @supports (-ms-ime-align: auto) {
        width: 30vw;
      }
    `}
  }
`;

export const marksChange = (params) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const {
        value,
        componentKey,
        bold,
        italic,
        underline,
        unorderedList,
        setMarkType,
        comment,
      } = params;
      const isChange = setMarksChange({
        value,
        bold,
        italic,
        underline,
        unorderedList,
        comment,
        setMarkType,
        componentKey,
      });
      if (isChange) resolve(null);
      resolve(true);
    }, 0);
  });
};

class BasicInput extends Component {
  constructor(props) {
    super(props);

    const { componentKey } = props;
    const { [componentKey]: value } = props;

    this.state = {
      value,
      showSnackBar: false,
      snackBarMessage: null,
      snackBarType: null,
    };
  }

  componentDidMount = () => {
    const {
      setInputEditorRef,
      setMarkType,
      componentKey,
      bold,
      italic,
      underline,
    } = this.props;

    setInputEditorRef(componentKey, this.editorRef);
    // Marks Setting it back to default
    if (italic !== false || bold !== false || underline !== false) {
      setMarkType({
        bold: false,
        italic: false,
        underline: false,
      });
    }
    const { editorRefs } = this.props;

    if (editorRefs) {
      const {
        value: {
          focusBlock,
          selection: { isFocused },
        },
      } = editorRefs[componentKey];
      const selections = editorRefs[componentKey];
      if (selections && focusBlock && isFocused) {
        selections?.deselect()?.blur();
      }
    }
  };

  componentDidUpdate = (prevProps) => {
    const { componentKey } = this.props;
    const {
      [componentKey]: { document: previousDocument },
      fileImported: previousIsFileImported,
      isStreamFinished: previousIsStreamFinished,
    } = prevProps;

    const {
      [componentKey]: incomingSection,
      updateEventTypes,
      eventTypes,
      fileImported: currentIsFileImported,
      isStreamFinished: currentIsStreamFinished,
    } = this.props;
    const { document: currentDocument } = incomingSection;

    if (previousDocument !== currentDocument) {
      if (
        eventTypes.length &&
        (eventTypes[eventTypes.length - 1].type === 'undo' ||
          eventTypes[eventTypes.length - 1].type === 'redo')
      ) {
        this.setState({ value: incomingSection });
        this.setButtonState(incomingSection);
        updateEventTypes({ isArray: true });
      } else if (
        currentIsFileImported &&
        previousIsFileImported !== currentIsFileImported
      ) {
        this.setState({ value: incomingSection });
      } else if (
        currentIsStreamFinished &&
        previousIsStreamFinished !== currentIsStreamFinished
      ) {
        this.setState({ value: incomingSection });
      }
    }
  };

  componentWillUnmount = () => {
    if (fnBrowserDetect() === SAFARI) {
      clearGlobalSelection();
    }
  };

  setButtonState = (incomingSection) => {
    const { setMarkType, bold, italic, underline } = this.props;
    const activeMarks = JSON.parse(JSON.stringify(incomingSection.activeMarks));
    const marks = {
      bold: false,
      italic: false,
      underline: false,
    };
    activeMarks.forEach(({ type }) => {
      if (type in marks) {
        marks[type] = true;
      }
    });
    if (
      bold !== marks.bold ||
      italic !== marks.italic ||
      underline !== marks.underline
    ) {
      setMarkType(marks);
    }
  };

  onChange = ({ value, operations }) => {
    const { document: currentDocument } = value;
    const { componentKey } = this.props;
    const {
      [componentKey]: { document: previousDocument },
      updateSection,
      setMarkType,
    } = this.props;

    const operationsArray = operations.toArray();

    const isComment = commentChange(operationsArray);

    let isSelectionOps = true;

    operationsArray.forEach((oper) => {
      if (
        oper.type !== SET_SELECTION &&
        !(
          oper.type === ADD_MARK &&
          markTypesToCheck.some((markType) => markType === oper.mark.type)
        ) &&
        !(
          oper.type === REMOVE_MARK &&
          markTypesToCheck.some((markType) => markType === oper.mark.type)
        )
      )
        isSelectionOps = false;
    });

    if (isComment) isSelectionOps = false;

    /**
     * Debounce only when user types rest of the events
     * dispatch immediately
     */
    const debounce = operationsArray.some(
      (operation) => operation.type === INSERT_TEXT,
    );

    /**
     * Remove marks from value, which are not required to be saved in the database
     * And avoid the marks to be updated to redux store
     * */
    const newValue = removeMarksFromValue(value);

    if (currentDocument !== previousDocument && !isSelectionOps) {
      clearTimeout(this.timer);
      if (debounce) {
        this.timer = setTimeout(() => {
          updateSection({
            updateType: 'component',
            key: componentKey,
            sectionKey: 'personalInfo',
            data: { value: newValue },
          });
          const { bold, italic, underline, comment } = this.props;
          setMarksChange({
            value: newValue,
            bold,
            italic,
            underline,
            comment,
            setMarkType,
          });
        }, TYPING_DEBOUNCE);
      } else {
        updateSection({
          updateType: 'component',
          key: componentKey,
          sectionKey: 'personalInfo',
          data: { value: newValue },
        });

        setTimeout(() => {
          const { bold, italic, underline, comment } = this.props;

          setMarksChange({
            value: newValue,
            bold,
            italic,
            underline,
            comment,
            setMarkType,
          });
        }, 0);
      }
    }
    if (isSelectionOps && value.selection.isFocused) {
      setTimeout(() => {
        const { bold, italic, underline, comment } = this.props;

        setMarksChange({
          value: newValue,
          bold,
          italic,
          underline,
          comment,
          setMarkType,
        });
      }, 0);
    }

    this.setState({ value });
  };

  onKeyDown = (event, editor, next) => {
    const { keyCode } = event;
    const {
      updatePopUpKeys,
      showCommentCard,
      showContextCard,
      editorRefs,
      formatType,
      dateRef,
      showExpertTips,
      setExpertTips,
    } = this.props;

    const {
      value: {
        document: { nodes },
        focusBlock,
      },
    } = editor;

    if (nodes.size) {
      const nodesArray = nodes.toArray();
      const [node] = nodesArray;
      const { type } = node;

      if (event.which === KEY_CODES.ENTER && type === 'letter_body') {
        const preventDefault = enter({ event, editor, nodeType: type });
        if (preventDefault) return;
      }
      if (event.which === KEY_CODES.BACKSPACE && type === 'letter_body') {
        const preventDefault = backspace({ event, editor, nodeType: type });
        if (preventDefault) return;
      }

      if (event.which === KEY_CODES.TAB) {
        const preventDefault = tab({
          event,
          editor,
          editorRefs,
          formatType,
          dateRef,
        });
        if (preventDefault) return;
      }
    }

    if (showExpertTips && keyCode === KEY_CODES.ESCAPE) {
      setExpertTips({ showExpertTips: false });
    }

    stopCommentExtension({
      editor: this.editorRef,
      showCommentCard,
      showContextCard,
      event,
      updatePopUpKeys,
    });

    const isUndoRedo = undoRedo(event);
    if (isUndoRedo) return;

    if (keyCode === KEY_CODES.ENTER) {
      event.preventDefault();
      return;
    }

    if (keyCode === KEY_CODES.BACKSPACE || keyCode === KEY_CODES.DELETE) {
      const isPreventDefault = removeSelectedNodes({
        editor,
        props: { updatePopUpKeys, showCommentCard, showContextCard },
        eventCode: keyCode,
      });
      if (isPreventDefault) return;
    }

    if (focusBlock) {
      const { type: focusBlockType } = focusBlock;

      /**
       * Prevent default in date components
       */
      if (dateComponents[focusBlockType]) {
        if (!allowedKeyCodes.includes(event.which)) {
          event.preventDefault();
        }
      }
    }

    next();
  };

  onPaste = (event, editor, next) => {
    const { formatType } = this.props;
    const isPreventDefault = paste({
      event,
      editor,
      formatType,
      setSnackBar: this.setSnackBar,
    });
    if (isPreventDefault) return;
    next();
  };

  renderMark = (...args) => {
    const argumentsData = args;
    const { formatType } = this.props;
    argumentsData[0].formatType = formatType;
    return renderMark(...argumentsData);
  };

  onFocus = async (_event, editor) => {
    const { componentKey, setInputEditorRef } = this.props;
    setInputEditorRef(componentKey);
    const { value } = this.state;

    /**
     * Show expert tips when focused for the first time
     */
    const { setExpertTips, showExpertTips } = this.props;
    const activeChip = nodeToInputNames[componentKey];

    if (showExpertTips) {
      setExpertTips({ activeChip });
    }

    const resp = await marksChange({ value, ...this.props });

    if (resp && fnBrowserDetect() !== CHROME) {
      editor.focus();
    }
    // if (resp && fnBrowserDetect() !== CHROME) next();
  };

  onBlur = (_event, editor, next) => {
    const {
      setInputEditorRef,
      setMarkType,
      componentKey,
      bold,
      italic,
      underline,
      comment,
    } = this.props;
    setInputEditorRef();
    setTimeout(() => {
      setMarksOnBlur({
        componentKey,
        bold,
        italic,
        underline,
        comment,
        setMarkType,
      });
      editor.blur();
    }, 0);
    next();
  };

  renderBlock = (props, editor, next) => {
    const { formatType, setDateRef, sectionKey, universityPlan, selectedPlan } =
      this.props;

    return renderBlock(
      props,
      editor,
      next,
      formatType,
      setDateRef,
      null,
      null,
      null,
      sectionKey,
      selectedPlan,
      universityPlan,
    );
  };

  handleClose = () => this.setState({ showSnackBar: false });

  setSnackBar = ({ snackBarMessage, snackBarType }) => {
    const { showSnackBar } = this.state;
    this.setState({
      snackBarMessage,
      snackBarType,
      showSnackBar: !showSnackBar,
    });
  };

  render() {
    const { value, showSnackBar, snackBarMessage, snackBarType } = this.state;
    const {
      label,
      componentKey,
      formatType,
      showCommentCard,
      showContextCard,
      permission,
      saveDocument,
      showImage,
    } = this.props;

    const { focusBlock } = value;
    let focusBlockType;
    if (focusBlock) {
      const { type } = focusBlock;
      focusBlockType = type;
    }

    const isFocusBlockComment =
      focusBlockType === showCommentCard.focusBlockType ||
      (focusBlockType &&
        focusBlockType.includes(showCommentCard.focusBlockType)) ||
      focusBlockType === showContextCard.focusBlockType ||
      (focusBlockType &&
        focusBlockType.includes(showContextCard.focusBlockType));

    return (
      <>
        {showSnackBar && (
          <SnackBarAlerts
            snackBarMessage={snackBarMessage}
            snackBarType={snackBarType}
            showSnackBar={showSnackBar}
            handleClose={this.handleClose}
          />
        )}

        <InputWrapper
          key={componentKey}
          formatType={formatType}
          label={label}
          componentKey={componentKey}
          showImage={showImage}
        >
          <Input componentKey={componentKey}>
            <Editor
              ref={(editor) => {
                this.editorRef = editor;
              }}
              plugins={resumePlugins}
              schema={Schema[componentKey]}
              value={value}
              onChange={this.onChange}
              onKeyDown={this.onKeyDown}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              renderMark={this.renderMark}
              onPaste={this.onPaste}
              renderNode={this.renderBlock}
              sectionKey={componentKey}
            />
            {isFocusBlockComment ? (
              <CommentsCard
                setSnackBarState={this.setSnackBarState}
                permission={permission}
                saveDocument={saveDocument}
                editorRef={this.editorRef}
                formatType={formatType}
              />
            ) : null}
          </Input>
        </InputWrapper>
      </>
    );
  }
}

BasicInput.propTypes = {
  componentKey: propTypes.string.isRequired,
  label: propTypes.string.isRequired,
  setInputEditorRef: propTypes.func.isRequired,
  updateSection: propTypes.func.isRequired,
  updateEditorReference: propTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const { componentKey, formatType } = ownProps;
  const common = {
    showCommentCard: state.commentsReducer.commentCard,
    showContextCard: state.commentsReducer.isContextMenu,
    bold: state.mostUsedToolsetReducer.marks.bold,
    italic: state.mostUsedToolsetReducer.marks.italic,
    underline: state.mostUsedToolsetReducer.marks.underline,
    comment: state.mostUsedToolsetReducer.marks.comment,
    activePlan: state.userInfoReducer.activePlan,
    subscriptionStatus: state.userInfoReducer.subscriptionStatus,
    inputIntro: state.userInfoReducer.inputIntro,
    showExpertTips: state.mostUsedToolsetReducer.showExpertTips,
    autoPromptExpertTips: state.mostUsedToolsetReducer.autoPromptExpertTips,
    user: state.user,
    universityPlan: state.userInfoReducer.universityPlan,
    selectedPlan: state.userInfoReducer.selectedPlan,
  };
  if (formatType === FormatTypes.RESUME) {
    return {
      [componentKey]: state.editResumeFormReducer[componentKey],
      eventTypes: state.editResumeFormReducer.eventTypes,
      fileImported: state.editResumeFormReducer.fileImported,
      isStreamFinished: state.editResumeFormReducer.isStreamFinished,
      ...common,
    };
  }

  if (formatType === FormatTypes.COVER_LETTER) {
    return {
      [componentKey]: state.editCoverLetterFormReducer[componentKey],
      eventTypes: state.editCoverLetterFormReducer.eventTypes,
      ...common,
    };
  }

  return null;
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { formatType } = ownProps;

  const common = {
    updatePopUpKeys: (params) => {
      dispatch(Actions.commentActions.updatePopUpKeys(params));
    },
    setMarkType: (params) =>
      dispatch(Actions.mostUsedToolsetActions.setMarkType(params)),
    setExpertTips: (params) =>
      dispatch(Actions.mostUsedToolsetActions.setExpertTips(params)),
    setInputIntro: (params) =>
      dispatch(Actions.userInfoActions.setInputIntro(params)),
  };

  if (formatType === FormatTypes.RESUME) {
    return {
      updateSection: (params) =>
        dispatch(Actions.editResumeFormActions.updateSection(params)),
      updateEventTypes: (params) =>
        dispatch(Actions.editResumeFormActions.updateEventTypes(params)),
      ...common,
    };
  }

  if (formatType === FormatTypes.COVER_LETTER) {
    return {
      updateSection: (params) =>
        dispatch(Actions.editCoverLetterFormActions.updateSection(params)),
      updateEventTypes: (params) =>
        dispatch(Actions.editCoverLetterFormActions.updateEventTypes(params)), // todo: merge conflict
      ...common,
    };
  }

  return null;
};

export default connect(mapStateToProps, mapDispatchToProps)(BasicInput);
